/**
 * Company Layout component
 * Provides navigation and layout structure for company dashboard pages
 */

import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Briefcase, 
  FileText, 
  Building, 
  Menu, 
  X, 
  LogOut,
  Bell,
  CheckCircle,
  Clock
} from 'lucide-react';
import { useCompanyAuth } from '../../core/contexts/CompanyAuthContext';

/**
 * Company Layout component
 * 
 * @returns {JSX.Element} Company layout with navigation
 */
const CompanyLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { companyProfile, logout, isVerified } = useCompanyAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Navigation links
  const navLinks = [
    { 
      name: 'Dashboard', 
      path: '/company/dashboard', 
      icon: <Home size={20} />,
      disabled: false
    },
    { 
      name: 'Job Postings', 
      path: '/company/jobs', 
      icon: <Briefcase size={20} />,
      disabled: false
    },
    { 
      name: 'Applications', 
      path: '/company/applications', 
      icon: <FileText size={20} />,
      disabled: false
    },
    { 
      name: 'Company Profile', 
      path: '/company/profile', 
      icon: <Building size={20} />,
      disabled: false
    }
  ];

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('companyAuthToken');
      navigate('/company/auth');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Check if link is active
  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Bar */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <Link to="/company/dashboard" className="flex items-center space-x-2">
                <div className="relative">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center transform rotate-12 shadow-lg">
                    <span className="text-white font-bold text-lg transform -rotate-12">V</span>
                  </div>
                  <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                </div>
                <div>
                  <span className="text-lg font-bold text-gray-800">
                    <span className="text-green-500">Varsity</span>Link
                  </span>
                  <div className="text-xs text-gray-500">Company Portal</div>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`flex items-center space-x-1 transition-colors relative ${
                    link.disabled
                      ? 'text-gray-400 cursor-not-allowed'
                      : isActive(link.path) 
                        ? 'text-green-500 font-medium'
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={link.disabled ? (e) => e.preventDefault() : undefined}
                >
                  {link.icon}
                  <span>{link.name}</span>
                  {link.disabled && (
                    <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity">
                      Verification required
                    </span>
                  )}
                </Link>
              ))}
            </div>

            {/* User Menu (Desktop) */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Verification Status */}
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                isVerified 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {isVerified ? (
                  <>
                    <CheckCircle className="w-3 h-3" />
                    <span>Verified</span>
                  </>
                ) : (
                  <>
                    <Clock className="w-3 h-3" />
                    <span>Pending</span>
                  </>
                )}
              </div>

              <button 
                className="text-gray-600 hover:text-gray-900 focus:outline-none"
                aria-label="Notifications"
              >
                <Bell size={20} />
              </button>

              <div className="flex items-center space-x-2">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-800">
                    {companyProfile?.companyName}
                  </div>
                  <div className="text-xs text-gray-500">
                    {companyProfile?.industry}
                  </div>
                </div>
                
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-semibold text-sm">
                    {companyProfile?.companyName?.charAt(0) || 'C'}
                  </span>
                </div>

                <button
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-red-500 focus:outline-none"
                  aria-label="Logout"
                >
                  <LogOut size={20} />
                </button>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 hover:text-gray-900 focus:outline-none"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-100 bg-white">
              {/* Company Info */}
              <div className="flex items-center space-x-3 mb-4 pb-4 border-b border-gray-100">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-semibold">
                    {companyProfile?.companyName?.charAt(0) || 'C'}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-800">
                    {companyProfile?.companyName}
                  </div>
                  <div className="text-sm text-gray-500 flex items-center space-x-2">
                    <span>{companyProfile?.industry}</span>
                    <div className={`flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs ${
                      isVerified 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {isVerified ? (
                        <>
                          <CheckCircle className="w-3 h-3" />
                          <span>Verified</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-3 h-3" />
                          <span>Pending</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            
              {/* Navigation Links */}
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`flex items-center space-x-3 py-3 px-2 rounded-lg ${
                    link.disabled
                      ? 'text-gray-400 cursor-not-allowed'
                      : isActive(link.path) 
                        ? 'bg-green-50 text-green-500 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => {
                    if (!link.disabled) {
                      setIsMenuOpen(false);
                    }
                  }}
                >
                  {link.icon}
                  <span>{link.name}</span>
                  {link.disabled && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                      Verification required
                    </span>
                  )}
                </Link>
              ))}
              
              {/* Mobile Logout */}
              <div className="border-t border-gray-100 mt-4 pt-4">
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-3 py-3 px-2 rounded-lg text-red-500 hover:bg-red-50 w-full"
                >
                  <LogOut size={20} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>
    </div>
  );
};

export default CompanyLayout;